/**
 * Auth store — Zustand.
 *
 * Holds the current user identity (NOT the access token — that stays in
 * `lib/auth/tokens.ts` as an in-memory module variable, never persisted).
 *
 * State shape mirrors `GET /api/v1/auth/me`.
 *
 * Consumer API (preserved across the prior RTK-shim era):
 *   const user = useAuthStore((s) => s.user);
 *   const { user, isLoading } = useAuthStore();
 *   useAuthStore.getState(); useAuthStore.setState({...});
 */

import { create } from 'zustand';

import type { UserMeResponse } from '@/lib/api/auth';

export interface AuthState {
  user: UserMeResponse | null;
  /** True until the initial /me check resolves on app boot. */
  isLoading: boolean;
  setUser: (user: UserMeResponse | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set: (partial: Partial<AuthState>) => void) => ({
  user: null,
  isLoading: true,
  setUser: (user: UserMeResponse | null) => set({ user }),
  setLoading: (loading: boolean) => set({ isLoading: loading }),
  logout: () => set({ user: null, isLoading: false }),
}));
